import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createElement, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ConfirmOptions } from '@/components/feedback/dialog';
import { useConfirm, WarningDiamondIcon } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import { mapDeviceKeys } from '@/features/devices';
import { mutationErrorMessage } from '@/lib/api/mutation-toast';

import {
  createField,
  deleteField,
  getField,
  getMapFields,
  importShapefile,
  updateField,
  validateBoundary,
} from './api';
import {
  exceedsImportSizeLimit,
  IMPORT_FILE_TOO_LARGE_MESSAGE,
  importErrorTone,
} from './import-refusal';
import { importStore } from './import-store';
import {
  FIELD_CREATED_MESSAGE,
  FIELD_UPDATED_MESSAGE,
  FIELD_UPLOADED_MESSAGE,
  IMPORT_TOAST_KEY,
  IMPORT_TOAST_SECONDS,
  type SaveFieldBody,
} from './schemas';

/**
 * Figma `1657:788465`. The **title is the design's** — the AC gives none, and "ACs beat designs"
 * resolves *conflicts*, not silences; a titleless warning dialog would also break the shipped
 * `<ConfirmDialog>` shape. The **body is the AC's, verbatim**.
 *
 * A `ConfirmOptions` const rather than an inline object literal, mirroring
 * `field-mapping.page.tsx`'s `SWITCH_CONFIRM`. Not exported and not in `schemas.ts`: one consumer
 * shares it, which is ADR-0010's narrowest scope, and the page tests assert the literal strings.
 *
 * `<WarningDiamondIcon>` rather than `<ConfirmDialog>`'s default trash glyph — the frame's 77 x 77
 * amber diamond (`1657:788474`) is that asset. Not `danger`: nothing is being deleted, and the
 * Grower's next step is to choose, not to destroy.
 */
const MULTIPLE_FIELDS_CONFIRM: ConfirmOptions = {
  title: 'Multiple Fields Found!',
  subtitle:
    'This file contains multiple fields. You can select and import only one field at a time.',
  confirmText: 'Continue',
  cancelText: 'Cancel',
  // `createElement` rather than `<WarningDiamondIcon />` so this file stays a `.ts`: all ten
  // feature `hooks.ts` modules in `src/features/*` are JSX-free, and renaming this one to `.tsx`
  // for a single icon would break that convention for no behaviour. The component takes no props,
  // so the call is exact.
  icon: createElement(WarningDiamondIcon),
};

export const fieldKeys = {
  all: ['fields'] as const,
  map: () => [...fieldKeys.all, 'map'] as const,
  detail: (id: string) => [...fieldKeys.all, 'detail', id] as const,
};

/**
 * The Boundary layer's whole payload. Its own query — so a Device-layer failure leaves the Fields
 * rendering, and vice versa (113586 §15b/§15c). Nothing structural is needed for that beyond the
 * fact that these are two endpoints rather than the admin twin's one snapshot call.
 *
 * **The Map is a load-time snapshot, not a live surface** (ADR-0016) — the same four settings ticket
 * 06 put on `useMapDevices()`, and for the same reasons, because the two layers must move together:
 * a Boundary appearing mid-pan while the Devices inside it do not would be incoherent.
 *
 * - no `refetchInterval` — nothing arrives on a timer;
 * - `refetchOnWindowFocus: false` — otherwise merely tabbing away past the app-wide 30s staleTime
 *   and back redraws the layer mid-session;
 * - `refetchOnReconnect: false` — the same door, opened by the network instead of the window. It
 *   costs nothing: `onOnline()` **also** calls `retryer.continue()`, which this flag does not gate,
 *   so a load paused by a dropped connection still completes by itself when it returns;
 * - `refetchOnMount: 'always'` — the half that must refetch. `<MapPage>` is a lazy route that
 *   genuinely unmounts, so reopening the Map is a real remount and 113586 §10e requires an edited
 *   Boundary to be redrawn there, even inside the staleTime.
 *
 * A refetch cannot move the Grower's viewport regardless: first-load framing is an uncontrolled
 * camera (ADR-0016).
 */
export function useMapFields() {
  return useQuery({
    queryKey: fieldKeys.map(),
    queryFn: getMapFields,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
  });
}

/** The focused Field's details. `null` while nothing is focused, which keeps the query idle rather
 *  than firing a request for an empty id. */
export function useField(fieldId: string | null) {
  return useQuery({
    queryKey: fieldKeys.detail(fieldId ?? ''),
    queryFn: () => getField(fieldId ?? ''),
    enabled: Boolean(fieldId),
  });
}

/**
 * Delete one Field (113586 §10a–§10d).
 *
 * **Invalidating `fieldKeys.map()` is the whole of "the overlay is removed" and "the legend adjusts
 * to the element types that remain"** (§10a, §10c). `<MapPage>` derives the drawn Boundaries, the
 * legend's two flags and the focus reconciliation from that one payload, so re-reading it is the only
 * change any of them needs — there is nothing to patch and no empty state to build.
 *
 * The **Device layer is deliberately left alone.** §10b requires the deleted Field's markers to stay
 * exactly as drawn, and their now-stale `fieldId` cannot be seen: both `labelledDeviceIds` and
 * `<DeviceMarkers>` key on the focused Field, and a deleted Field can never be focused again.
 * Refetching it would also risk showing a Device newly placed mid-session, which 113587 §1f forbids.
 *
 * `suppressToast` because the caller owns both halves of the feedback: §10a's confirmation is a toast
 * with exact copy, and §10d's failure is a retryable region notice rather than a toast, which the
 * global `<MutationToaster>` cannot render.
 */
export function useDeleteField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteField,
    meta: { suppressToast: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fieldKeys.map() }),
  });
}

/**
 * One **Placement verdict** round trip.
 *
 * `suppressToast` because `<MutationToaster>`'s rule does not fit here: a per-**Point** refusal is a
 * verdict inside a *successful* 200, not a mutation outcome, and the marking flow owns both it and
 * the failure case as one keyed 5 s toast (`useFieldPoints`). Nothing is invalidated — this writes
 * nothing.
 */
export function useValidateBoundary() {
  return useMutation({ mutationFn: validateBoundary, meta: { suppressToast: true } });
}

/**
 * Create the Field.
 *
 * `meta.successMessage` rather than the response envelope's `message` — `<MutationToaster>` is the
 * app's one announcement point and the copy is ours (grilling Q14). Failures fall through to its
 * generic error toast, which is what leaves the modal's own inline errors as the only *field-level*
 * presentation.
 *
 * Both map queries are invalidated and **nothing is hand-patched**: a new Field releases Devices
 * from whatever they belonged to before, and that arithmetic is the server's. Belt-and-braces
 * anyway — `<MapPage>` is a lazy route whose `useMapFields()` sets `refetchOnMount: 'always'`, so
 * navigating back refetches regardless.
 */
export function useCreateField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createField,
    meta: { successMessage: FIELD_CREATED_MESSAGE },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.map() });
      void queryClient.invalidateQueries({ queryKey: mapDeviceKeys.map() });
    },
  });
}

/**
 * Update the Field. `useCreateField`'s sibling, and deliberately a second hook rather than a
 * `useSaveField(id | null)`: the create hook is shipped and tested, and one `meta` constant is the
 * only real difference.
 *
 * Both map queries are invalidated and **nothing is hand-patched** — an edited Boundary releases
 * Devices it no longer encloses and claims ones it now does, and that arithmetic is the server's.
 * This is the whole of "Devices are re-associated by their own locations on re-render".
 */
export function useUpdateField(fieldId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveFieldBody) => updateField(fieldId, body),
    meta: { successMessage: FIELD_UPDATED_MESSAGE },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.map() });
      void queryClient.invalidateQueries({ queryKey: mapDeviceKeys.map() });
    },
  });
}

/**
 * The whole import gesture, from a chosen file to the **Import preview** — the parse, the
 * announcement, the handoff and the navigation.
 *
 * **`meta.suppressToast`, and the feature presents both halves itself**
 * ([ADR-0025](../../../docs/adr/0025-import-toast-tone-by-error-code.md)). That is forced, not
 * preferred, by two independent facts. The success toast is **response**-conditional — 113609 shows
 * "Field uploaded successfully" only when *one* shape is imported, and a multi-shape parse shows the
 * confirmation and no toast — while `meta.successMessage` receives the mutation's *variables* and
 * never its response, so `<MutationToaster>` cannot express the condition at all. And
 * `FIELD_IMPORT_INVALID_FORMAT` (400) and `FIELD_IMPORT_NO_POLYGON` (422) deliberately share the
 * string "Invalid file format.", so a status-keyed tone would paint one sentence in two colours.
 *
 * **It lives here rather than in `<MapPage>` because that same ADR puts it here**: its Decision
 * reads "the **feature** presents both halves itself" and its Consequences pin "Both toast halves
 * for import live at one call site in `features/fields`, so the response-conditional success toast
 * and the code-keyed failure toast are read together rather than split". Ticket 03's five failure
 * toasts and its code→tone map land in this function, next to the success half — not in the
 * 933-line `<MapPage>`, which is the file grilling Q2 exists to keep from growing.
 *
 * The page keeps only what is genuinely the page's: which control is open. It calls `upload(file)`
 * and nothing else.
 *
 * A `requiresSelection` parse raises the "Multiple Fields Found!" confirmation off the same branch
 * — see `MULTIPLE_FIELDS_CONFIRM` above — with **no toast**, and it navigates only if the Grower
 * chooses Continue.
 *
 * **Nothing is invalidated: this call writes nothing.** A Field appears in the Boundary layer only
 * when the Grower saves one, which is `useCreateField`'s job.
 *
 * **Failure (119739).** Tone comes from {@link importErrorTone} — ADR-0025's five-code map with a
 * fall-through to the app-wide `mutationErrorTone` — and copy from `mutationErrorMessage`, which is
 * the same "prefer `error.message`" rule (ADR-0008) `<MutationToaster>` applies. Success and
 * failure share {@link IMPORT_TOAST_KEY}, so triggering the same refusal twice restarts the 5 s
 * timer instead of stacking a second toast.
 *
 * **The `AbortController` is load-bearing, not a nicety:** no request timeout is configured
 * anywhere in this app, so without it a stalled upload would hold the Add Field flow's lock until a
 * page reload. An abandoned upload is silent — the Grower asked for it to stop, so announcing it
 * would be noise — which is why `onError` checks the signal before toasting.
 */
export function useShapefileImport() {
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();
  // One controller per in-flight upload. A ref rather than state: nothing renders from it, and the
  // `×` reads it in an event handler (the same reasoning ADR-0016 gives for the map view store).
  const abortRef = useRef<AbortController | null>(null);

  // `mutate` destructured rather than the whole result kept: `useMutation`'s return is not
  // referentially stable, so depending on it below would rebuild `upload` on every render — which
  // `@tanstack/query/no-unstable-deps` catches. `mutate` itself is stable. `isPending` drives the
  // parse lock's pending tile (ticket 03).
  const { mutate, isPending } = useMutation({
    mutationFn: ({ file, signal }: { file: File; signal?: AbortSignal }) =>
      importShapefile(file, signal),
    meta: { suppressToast: true },
  });

  const upload = useCallback(
    (file: File, options?: { onSettled?: () => void }) => {
      // The one client-side judgement (grilling L8). Same copy and same tone as the server's 413,
      // so the two refusals are indistinguishable to the Grower — which is the point.
      if (exceedsImportSizeLimit(file.size)) {
        toast.warning({
          title: IMPORT_FILE_TOO_LARGE_MESSAGE,
          key: IMPORT_TOAST_KEY,
          duration: IMPORT_TOAST_SECONDS,
        });
        options?.onSettled?.();
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      mutate(
        { file, signal: controller.signal },
        {
          onSuccess: (imported) => {
            /**
             * More than one importable shape ⇒ the Grower chooses, and **no success toast fires**:
             * the AC scopes "Field uploaded successfully" to a single-shape parse.
             * `requiresSelection` is the server's own flag and is computed over shapefile holes
             * too, which is exactly why nothing here filters by `kind`.
             *
             * The store is written **only** on Continue, so Cancel / the `x` / Escape discard the
             * imported file entirely with nothing to clear — all three land on `settle(false)`
             * inside `<DialogHost>`, and none of them navigates. `void confirm` with an
             * `onConfirm`, the shipped idiom `<MapPage>`'s own `requestDelete` uses.
             */
            if (imported.requiresSelection) {
              void confirm({
                ...MULTIPLE_FIELDS_CONFIRM,
                onConfirm: () => {
                  importStore.save(imported);
                  navigate(routes.fieldImport);
                },
              });
              return;
            }
            importStore.save(imported);
            toast.success({
              title: FIELD_UPLOADED_MESSAGE,
              key: IMPORT_TOAST_KEY,
              duration: IMPORT_TOAST_SECONDS,
            });
            navigate(routes.fieldImport);
          },
          onError: (error) => {
            // Abandoning is not a failure to report: the Grower is the one who ended it.
            if (controller.signal.aborted) return;
            toast[importErrorTone(error)]({
              title: mutationErrorMessage(error),
              key: IMPORT_TOAST_KEY,
              duration: IMPORT_TOAST_SECONDS,
            });
          },
          onSettled: () => {
            abortRef.current = null;
            options?.onSettled?.();
          },
        },
      );
    },
    [confirm, mutate, navigate, toast],
  );

  /** Abandon an upload in flight. Safe to call when nothing is running — the ref is already null
   *  then, and `onSettled` is what clears it after a real abort. */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { upload, cancel, isPending };
}
